import { booleanAttribute, ChangeDetectionStrategy, Component, computed, DestroyRef, DOCUMENT, ElementRef, inject, input, model, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RadButtonComponent } from '../button/button.component';

// The panel's width, as a multiple of the vendor's base text size, the way the control widths are.
const SIZES = {
    md: 'calc(var(--main-font-size) * 40)',
    lg: 'calc(var(--main-font-size) * 45)',
} as const;

// A panel docked to the right edge of the viewport. It draws a header carrying the title and the
// close, a body that scrolls, and a footer for actions; the body content and the footer actions are
// projected.
//
// The page behind an open panel goes on working. A panel that sets modal lays a scrim over the
// page instead, and nothing behind that scrim can be clicked or tabbed to. See side-panel.mdx.
@Component({
    selector: 'rad-side-panel',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RadButtonComponent],
    host: {
        '[class.open]': 'open()',
        // Without a scrim the host has to let the page under it take the clicks it would have
        // caught, which is the one thing the styles need to know.
        '[class.no-scrim]': '!modal()',
        '[style.--rad-side-panel-width]': 'width()',
        // Closed, the whole panel leaves the tab order and the accessibility tree, so nothing in it
        // can be reached while its markup stays in the DOM.
        '[attr.inert]': 'open() ? null : ""',
        // A static attribute in a template is written to the DOM as well as matched to an input, so
        // <rad-side-panel title="…"> leaves a title attribute on the host. The scrim is inside that
        // host, so the browser draws its own tooltip over the page the panel is covering. Clearing
        // it keeps the input and drops the tooltip.
        '[attr.title]': 'null',
        '(document:keydown.escape)': 'dismiss()',
    },
    template: `
        <!--
            The scrim projects nothing, so writing it out is safe where writing the panel out is
            not — see the Closed section of side-panel.mdx.
        -->
        @if (modal()) {
            <div class="scrim" (click)="dismissFromScrim()"></div>
        }

        <!--
            tabindex="-1" so the panel can take focus when it opens without becoming a tab stop of
            its own once the reader is inside it.
        -->
        <aside
            #panel
            class="panel"
            role="dialog"
            tabindex="-1"
            [attr.aria-modal]="modal() ? 'true' : null"
            [attr.aria-label]="title()"
        >
            <div class="header">
                <span class="title">{{ title() }}</span>
                <button rad-button variant="ghost" size="sm" icon="fa:xmark" [attr.aria-label]="closeLabel()" (click)="dismiss()"></button>
            </div>

            <div class="body" [style.padding]="bodyPadding()"><ng-content /></div>

            <div class="footer"><ng-content select="[footer]" /></div>
        </aside>
    `,
    styleUrl: './side-panel.component.scss',
})
export class RadSidePanelComponent {

    /** Whether the panel is showing. Two-way, so the screen that opened it is also told when it closes. */
    readonly open = model(false);

    /** The panel's heading, drawn uppercased in the header and used as the panel's accessible name. */
    readonly title = input.required<string>();

    /** The accessible name of the close control. */
    readonly closeLabel = input('Close');

    /** Whether a scrim covers the page behind the panel, taking its clicks and its tab stops. */
    readonly modal = input(false, { transform: booleanAttribute });

    /** Whether a click on the scrim closes the panel. Escape and the close control close it either way. */
    readonly scrimClose = input(true, { transform: booleanAttribute });

    /** The body's padding, as a step from the spacing scale. Content that dresses itself takes `0`. */
    readonly padding = input<'0' | '6'>('6');

    // The scale starts at one, so padding="0" names a variable nothing declares and the body takes
    // the fallback instead.
    protected readonly bodyPadding = computed(() => `var(--rad-space-${this.padding()}, 0)`);

    /** The panel's width, `md` or `lg`. On a screen narrower than the panel, both stop short of the screen's edge. */
    readonly size = input<keyof typeof SIZES>('md');

    protected readonly width = computed(() => SIZES[this.size()]);

    private readonly panel = viewChild.required<ElementRef<HTMLElement>>('panel');
    private readonly body = inject(DOCUMENT).body;

    // Where focus was when the panel opened, so it can be put back when the panel closes.
    private opener: HTMLElement = null;

    constructor() {
        toObservable(this.open).pipe(takeUntilDestroyed()).subscribe(open => {
            this.moveFocus(open);
            this.markBodyModal(open);
        });
        inject(DestroyRef).onDestroy(() => this.markBodyModal(false));
    }

    // The panel is fixed at the modal z-index, so a KUI dropdown mounted on <body> would open
    // behind it. KUI lifts dropdowns above a modal while <body> carries modal-open, and the panel
    // joins that convention rather than adding a second one.
    private markBodyModal(open: boolean): void {
        this.body.classList.toggle('modal-open', open);
    }

    // Escape closes from anywhere, including from a control inside the panel. The guard is what
    // keeps a closed panel from swallowing the key from whatever else is listening for it.
    protected dismiss(): void {
        if (this.open()) {
            this.open.set(false);
        }
    }

    protected dismissFromScrim(): void {
        if (this.scrimClose()) {
            this.dismiss();
        }
    }

    // A hidden element cannot take focus, so the panel is focused on the turn after the class that
    // reveals it lands rather than in the same one.
    private moveFocus(open: boolean): void {
        if (open) {
            this.opener = document.activeElement as HTMLElement;
            setTimeout(() => this.panel().nativeElement.focus());
        } else {
            this.opener?.focus();
            this.opener = null;
        }
    }
}
